import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Layout,
  Row,
  Select,
  Radio,
  Upload,
  AutoComplete,
  message,
} from "antd";
import { useWatch } from "antd/es/form/Form";
import { Trash, Plus, Inbox, ExternalLink } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import ViewAttachmentModal from "../../Components/CustomModals/ViewAttachmentModal";
import ReasonModal from "../../Components/CustomModals/ReasonModal";
import ConfirmAction from "../../Components/ConfirmAction/ConfirmAction";
import {
  formatNumbers,
  formatString,
  navigateTo,
  getUserBranch,
} from "../../Utils/Common";
import { getRequest, postRequest } from "../../Utils/ApiCalls";
import dayjs from "dayjs";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";
import { useParams } from "react-router-dom";

const { Dragger } = Upload;
const { Option } = Select;

// ── "Learning" item name autocomplete ──
// Suggests item names previously fetched from the backend, plus any
// brand-new names the user types (remembered locally via localStorage),
// so the field "learns" new items even before they exist server-side.
const ITEM_NAME_STORAGE_KEY = "purchase_request_item_names";

function getLearnedItemNames(): string[] {
  try {
    const saved = JSON.parse(localStorage.getItem(ITEM_NAME_STORAGE_KEY) || "[]");
    return Array.isArray(saved) ? saved : [];
  } catch {
    return [];
  }
}

function rememberItemName(name: string) {
  const trimmed = name?.trim();
  if (!trimmed) return;
  const learned = getLearnedItemNames();
  if (learned.includes(trimmed)) return;
  const updated = [trimmed, ...learned].slice(0, 50); // cap history
  localStorage.setItem(ITEM_NAME_STORAGE_KEY, JSON.stringify(updated));
}

interface PurchaseRequestFormProps {
  add?: boolean;
  edit?: boolean;
  view?: boolean;
  approval?: boolean;
}

const PurchaseRequestForm: React.FC<PurchaseRequestFormProps> = ({
  add,
  edit,
  view,
  approval,
}) => {
  const [loading,               setLoading]               = useState("");
  const [form]                                            = Form.useForm();
  const [expenseTypes,          setExpenseTypes]          = useState<any[]>([]);
  const [branches,              setBranches]              = useState<any[]>([]);
  const [suppliers,             setSuppliers]             = useState<any[]>([]);
  const [items,                 setItems]                 = useState<any[]>([]);
  const [itemNameOptions,       setItemNameOptions]       = useState<{ value: string }[]>([]);
  const [withholdingTaxPercent, setWithholdingTaxPercent] = useState(0);
  const [showDisapproveModal,   setShowDisapproveModal]   = useState(false);
  const [disapproveRemarks,     setDisapproveRemarks]     = useState("");
  const [attachmentUrls,        setAttachmentUrls]        = useState<string[]>([]);
  const [showAttachment,        setShowAttachment]        = useState<string | null>(null);

  const itemsArray = useWatch("itemsArray", form);
  const vatType    = useWatch("vat_type", form);
  const { id: purchase_request_id } = useParams<{ id: string }>();

  const isDisabled = view || approval;

  function clearFields() {
    form.resetFields();
  }

  function refreshItemNameOptions() {
    const backendNames = items.map((i: any) => i.name).filter(Boolean);
    const learnedNames = getLearnedItemNames();
    const allNames = Array.from(new Set([...backendNames, ...learnedNames]));
    setItemNameOptions(allNames.map((name) => ({ value: name })));
  }

  function handleItemNameBlur(e: React.FocusEvent<HTMLInputElement>) {
    rememberItemName(e.target.value);
    refreshItemNameOptions();
  }

  const subTotal             = (itemsArray || []).reduce((sum: number, item: any) => {
    return sum + (Number(item?.qty) || 0) * (Number(item?.price) || 0);
  }, 0);
  const vatAmount            = subTotal - subTotal / 1.12;
  const netVat               = subTotal / 1.12;
  const withholdingTaxAmount = subTotal * (withholdingTaxPercent / 100);
  const grandTotal           = subTotal - withholdingTaxAmount;

  const handleBeforeUpload = (file: any) => {
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error(`${file.name} exceeds the 2MB size limit.`);
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const handleUploadChange = (info: any) => {
    const validFiles = info.fileList.filter((f: any) => f.size / 1024 / 1024 < 2);
    form.setFieldValue("attachment", validFiles);
  };

  async function submitForm(data: any) {
    const formattedItemsArray = (data.itemsArray || []).map((item: any) => ({
      ...item,
      total_amount: parseFloat(
        ((Number(item.qty) || 0) * (Number(item.price) || 0)).toFixed(2)
      ),
    }));

    // Remember every item name used in this submission for future suggestions.
    formattedItemsArray.forEach((item: any) => rememberItemName(item.item_name));

    const formData = new FormData();
    formData.append("remarks",        data.remarks ?? "");
    formData.append("expense_date",   data.expense_date ? dayjs(data.expense_date).format("YYYY-MM-DD") : "");
    formData.append("itemsArray",     JSON.stringify(formattedItemsArray));
    formData.append("grand_total",    String(grandTotal));
    formData.append("wht_percentage", String(withholdingTaxPercent));
    formData.append("wht_amount",     String(withholdingTaxAmount));
    formData.append("vat_type",       vatType ?? "");
    formData.append("supplier_name",  String(data.supplier_name ?? ""));
    formData.append("branch_id",      String(data.branch_id ?? ""));
    formData.append("requested_by",   String(data.requested_by ?? ""));
    formData.append("payment_method", String(data.payment_method ?? ""));
    if (edit) formData.append("purchase_request_id", String(purchase_request_id));

    const attachments = data.attachment || [];
    attachments.forEach((file: any) => {
      if (file.originFileObj) formData.append("attachment[]", file.originFileObj);
    });

    setLoading("submit");
    const endpoint = add ? "purchase_requests/create" : "purchase_requests/update";
    const response = await postRequest(endpoint, formData, true);

    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => { clearFields(); setLoading(""); navigateTo("/purchase-request"); }, 1000);
    } else {
      setLoading("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          `Failed to ${add ? "create" : "update"} purchase request.`
      );
    }
  }

  async function submitApprove() {
    setLoading("approve");
    const response = await postRequest(
      `purchase_requests/change_status`,
      { purchase_request_id, action: "approved" },
      true
    );
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => { clearFields(); setLoading(""); navigateTo("/purchase-request"); }, 1000);
    } else {
      setLoading("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          "Failed to approve purchase request."
      );
    }
  }

  async function submitDisapprove() {
    if (!disapproveRemarks.trim()) {
      message.error("Please provide a reason for disapproval.");
      return;
    }
    setShowDisapproveModal(false);
    setLoading("disapprove");
    const response = await postRequest(
      `purchase_requests/change_status`,
      { purchase_request_id, action: "disapproved", reason: disapproveRemarks },
      true
    );
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => { clearFields(); setLoading(""); navigateTo("/purchase-request"); }, 1000);
    } else {
      setLoading("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          "Failed to disapprove purchase request."
      );
    }
  }

  async function fetchBranches() {
    const response = await getRequest(`branches/get`, {}, true);
    if (response.success) {
      setBranches(response.data.data);

      const userBranchId = getUserBranch();
      if (userBranchId) {
        form.setFieldValue("branch_id", Number(userBranchId));
      }
    }
  }

  async function fetchExpenseTypes() {
    setExpenseTypes([]);
    const response = await getRequest(`expense_types/get`, {}, true);
    if (response.success) setExpenseTypes(response.data.data);
  }

  async function fetchSuppliers() {
    setSuppliers([]);
    const response = await getRequest(`suppliers/get`, {}, true);
    if (response.success) setSuppliers(response.data.data);
  }

  async function fetchItems() {
    setItems([]);
    const response = await getRequest(`items/get`, {}, true);
    if (response.success) setItems(response.data.data);
  }

  useEffect(() => {
    if ((edit || view || approval) && expenseTypes.length > 0) {
      setLoading("fetch");
      getRequest(`purchase_requests/get`, { purchase_request_id }, true).then((response) => {
        if (response.success) {
          const data = response.data.data[0];
          form.setFieldsValue({
            supplier_name:  data.supplier_name,
            expense_date:   data.expense_date ? dayjs(data.expense_date) : null,
            branch_id:      Number(data.branch_id),
            requested_by:   data.requested_by,
            remarks:        data.remarks,
            vat_type:       data.vat_type,
            payment_method: data.payment_method,
            itemsArray:     data.items.map((row: any) => ({
              item_id:         row.item_id,
              item_name:       row.item_name,
              expense_type_id: Number(row.expense_type_id),
              qty:             Number(row.qty),
              unit:            row.unit,
              price:           Number(row.price),
            })),
            wht_percentage: data.wht_percentage,
            wht_amount:     data.wht_amount,
          });
          if (data.wht_percentage) setWithholdingTaxPercent(Number(data.wht_percentage));

          // ── Support both array and legacy single URL ──
          if (Array.isArray(data.attachments)) {
            setAttachmentUrls(data.attachments);
          } else if (data.attachment_url) {
            setAttachmentUrls([data.attachment_url]);
          } else {
            setAttachmentUrls([]);
          }
        }
        setLoading("");
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenseTypes]);

  useEffect(() => {
    fetchExpenseTypes();
    fetchBranches();
    fetchSuppliers();
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rebuild the "learned" item name suggestions whenever the backend item list changes.
  useEffect(() => {
    refreshItemNameOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const pageTitle = add      ? "Add Purchase Request"
    : edit         ? "Edit Purchase Request"
    : approval     ? "Approve Purchase Request"
    :                "View Purchase Request";

  return (
    <Layout className="page">
      <Helmet><title>Agua Lab ERP System</title></Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader title={pageTitle} withDateRangeFilter={false} withAddButton={false} withSearchBtn={false} />
          </Col>

          <div className="form-container">
            <Col span={24}>
              <Form form={form} onFinish={submitForm} layout="vertical">
                <Row className="w-full" gutter={[10, 10]}>

                  <Col xs={24} lg={8}>
                    <Form.Item
                      label="Supplier Name"
                      name="supplier_name"
                      rules={[{ required: !isDisabled, whitespace: true, message: "This field is required." }]}
                    >
                      <Input disabled={isDisabled} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} lg={8}>
                    <Form.Item
                      label="Transaction Date"
                      name="expense_date"
                      rules={[{ required: !isDisabled, message: "This field is required." }]}
                    >
                      <DatePicker disabled={isDisabled} className="w-full" format="MMMM D, YYYY" />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={8}>
                    <Form.Item
                      label="Branch"
                      name="branch_id"
                      rules={[{ required: !isDisabled, message: "This field is required." }]}
                    >
                      <Select
                        placeholder="Select branch"
                        showSearch
                        allowClear
                        optionFilterProp="label"
                        options={branches.map((b: any) => ({ label: b.name, value: Number(b.id) }))}
                        disabled={isDisabled || add || edit}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} lg={8}>
                    <Form.Item
                      label="Requested By"
                      name="requested_by"
                      rules={[{ required: !isDisabled, whitespace: true, message: "This field is required." }]}
                    >
                      <Input disabled={isDisabled} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} lg={8}>
                    <Form.Item
                      label="Vat Type"
                      name="vat_type"
                      rules={[{ required: !isDisabled, message: "This field is required." }]}
                    >
                      <Select
                        placeholder="Select Vat Type"
                        showSearch
                        allowClear
                        disabled={isDisabled}
                        filterOption={(input: string, option?: any) =>
                          option?.children?.toLowerCase().includes(input.toLowerCase())
                        }
                      >
                        <Option value="vat-in">Vat In</Option>
                        <Option value="vat-ex">Vat Ex</Option>
                        <Option value="zero-rated">Zero-Rated</Option>
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24} lg={8}>
                    <Form.Item
                      label="Payment Method"
                      name="payment_method"
                      rules={[{ required: !isDisabled, message: "This field is required." }]}
                    >
                      <Select
                        placeholder="Select Payment Method"
                        showSearch
                        allowClear
                        disabled={isDisabled}
                        filterOption={(input: string, option?: any) =>
                          option?.children?.toLowerCase().includes(input.toLowerCase())
                        }
                      >
                        <Option value="check">Check</Option>
                        <Option value="credit">Credit</Option>
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24}>
                    <Form.Item label="Remarks" name="remarks">
                      <Input.TextArea disabled={isDisabled} />
                    </Form.Item>
                  </Col>

                  <Row>
                    <span className="edit-label mb-2">
                      {!isDisabled && <label className="text-red-500 me-1">*</label>}
                      List of Items:
                    </span>
                    <Form.List name="itemsArray" initialValue={[{}]}>
                      {(fields, { add: addRow, remove }) => (
                        <>
                          <Col xs={24}>
                            <div className="overflow-auto max-h-[600px]">
                              <table className="w-full table-fixed border border-collapse min-w-[800px]">
                                <thead>
                                  <tr className="bg-primary text-white font-bold border">
                                    <th className="p-2 border w-[100px]">Item Name</th>
                                    <th className="p-2 border w-[80px]">Expense Type</th>
                                    <th className="p-2 border w-[50px]">Qty</th>
                                    <th className="p-2 border w-[50px]">Unit</th>
                                    <th className="p-2 border w-[60px]">Price</th>
                                    <th className="p-2 border w-[60px]">Total</th>
                                    {!isDisabled && (
                                      <th className="p-2 border w-[40px] text-center">Action</th>
                                    )}
                                  </tr>
                                </thead>
                                <tbody>
                                  {fields.map((field) => (
                                    <tr key={field.key} className="border bg-white">
                                      <td className="p-2 border">
                                        <Form.Item name={[field.name, "item_name"]} rules={[{ required: !isDisabled, message: "Required" }]} noStyle>
                                          <AutoComplete
                                            className="w-full"
                                            disabled={isDisabled}
                                            options={itemNameOptions}
                                            filterOption={(inputValue, option) =>
                                              (option?.value as string ?? "")
                                                .toLowerCase()
                                                .includes(inputValue.toLowerCase())
                                            }
                                            onBlur={handleItemNameBlur}
                                          />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item name={[field.name, "expense_type_id"]} rules={[{ required: !isDisabled, message: "Required" }]} noStyle>
                                          <Select
                                            className="w-full"
                                            placeholder="Select expense type"
                                            showSearch
                                            allowClear
                                            optionFilterProp="label"
                                            options={expenseTypes.map((t: any) => ({ label: t.name, value: Number(t.id) }))}
                                            disabled={isDisabled}
                                          />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item name={[field.name, "qty"]} rules={[{ required: !isDisabled, message: "Required" }]} noStyle>
                                          <Input type="number" min={1} disabled={isDisabled} onWheel={(e) => (e.target as HTMLInputElement).blur()} />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item name={[field.name, "unit"]} rules={[{ required: !isDisabled, message: "Required" }]} noStyle>
                                          <Input type="text" disabled={isDisabled} />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item name={[field.name, "price"]} rules={[{ required: !isDisabled, message: "Required" }]} noStyle>
                                          <Input type="number" min={0} step="0.01" disabled={isDisabled} onWheel={(e) => (e.target as HTMLInputElement).blur()} />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item shouldUpdate noStyle>
                                          {() => {
                                            const qty   = form.getFieldValue(["itemsArray", field.name, "qty"]);
                                            const price = form.getFieldValue(["itemsArray", field.name, "price"]);
                                            const total = (Number(qty) || 0) * (Number(price) || 0);
                                            return <Input value={formatNumbers(total)} disabled />;
                                          }}
                                        </Form.Item>
                                      </td>
                                      {!isDisabled && (
                                        <td className="p-2 border text-center">
                                          {fields.length > 1 && (
                                            <Button onClick={() => remove(field.name)} icon={<Trash size={17} />} danger type="text" />
                                          )}
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </Col>

                          <Col xs={24}>
                            {!isDisabled && (
                              <Row className="mt-2">
                                <Col span={17}>
                                  <Button type="primary" onClick={() => addRow()} icon={<Plus size={16} />}>Add Item</Button>
                                </Col>
                              </Row>
                            )}
                            <Row className="mt-2">
                              <Col span={19} className="text-end"><strong>Subtotal:</strong></Col>
                              <Col span={5} className="text-start ps-5">₱ {formatNumbers(subTotal)}</Col>
                            </Row>
                            {vatType === "vat-in" && (
                              <>
                                <Row className="mt-2">
                                  <Col span={19} className="text-end"><strong>12% Vat:</strong></Col>
                                  <Col span={5} className="text-start ps-5">₱ {formatNumbers(vatAmount)}</Col>
                                </Row>
                                <Row className="mt-2">
                                  <Col span={19} className="text-end"><strong>Net of Vat:</strong></Col>
                                  <Col span={5} className="text-start ps-5">₱ {formatNumbers(netVat)}</Col>
                                </Row>
                              </>
                            )}
                            <Row className="mt-2">
                              <Col span={19} className="text-end"><strong>Withholding Tax:</strong></Col>
                              <Col span={5} className="text-start ps-1">
                                {isDisabled ? (
                                  <span className="ps-4">{withholdingTaxPercent}%</span>
                                ) : (
                                  <Radio.Group
                                    value={withholdingTaxPercent}
                                    onChange={(e) => setWithholdingTaxPercent(e.target.value)}
                                  >
                                    <Radio value={0}>0%</Radio>
                                    <Radio value={1}>1%</Radio>
                                    <Radio value={2}>2%</Radio>
                                  </Radio.Group>
                                )}
                              </Col>
                            </Row>
                            <Row className="mt-2">
                              <Col span={19} className="text-end"><strong>Calculated Withholding Tax:</strong></Col>
                              <Col span={5} className="text-start ps-5">₱ {formatNumbers(withholdingTaxAmount)}</Col>
                            </Row>
                            <Row className="mt-2">
                              <Col span={19} className="text-end"><strong>Grand Total:</strong></Col>
                              <Col span={5} className="text-start ps-5">₱ {formatNumbers(grandTotal)}</Col>
                            </Row>
                          </Col>
                        </>
                      )}
                    </Form.List>
                  </Row>

                  {/* ── View attachments (read-only) ── */}
                  {(view || approval) && attachmentUrls.length > 0 && (
                    <Col xs={24} className="pt-1 mt-4">
                      <span className="font-bold block mb-2">Attachment(s)</span>
                      <div className="flex flex-wrap gap-2">
                        {attachmentUrls.map((url, index) => (
                          <Button
                            key={index}
                            icon={<ExternalLink size={14} />}
                            onClick={() => setShowAttachment(url)}
                          >
                            Attachment {index + 1}
                          </Button>
                        ))}
                      </div>
                    </Col>
                  )}

                  {/* ── Upload (add / edit) ── */}
                  {(add || edit) && (
                    <Col xs={24} className="mt-5 pt-5">
                      <Form.Item
                        label="Attachment(s)"
                        name="attachment"
                        valuePropName="fileList"
                        getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList}
                      >
                        <Dragger
                          name="file"
                          multiple
                          beforeUpload={handleBeforeUpload}
                          onChange={handleUploadChange}
                          accept=".pdf,image/*"
                        >
                          <p className="ant-upload-drag-icon flex justify-center items-center text-3xl"><Inbox /></p>
                          <p className="ant-upload-text">Click or drag files to upload</p>
                          <p className="ant-upload-hint">Supports PDF, Image formats. You can upload multiple files.</p>
                        </Dragger>
                        <p className="text-textColor pt-2">Note: Maximum file size is 2MB per file.</p>
                      </Form.Item>
                    </Col>
                  )}

                  <Col lg={24} className="mt-5 flex justify-end gap-3">
                    <Form.Item>
                      <Button onClick={() => { navigateTo("/purchase-request"); clearFields(); }}>
                        {isDisabled ? "Close" : "Cancel"}
                      </Button>
                    </Form.Item>

                    {approval && (
                      <>
                        <Form.Item>
                          <Button
                            danger
                            loading={loading === "disapprove"}
                            onClick={() => { setDisapproveRemarks(""); setShowDisapproveModal(true); }}
                          >
                            Disapprove
                          </Button>
                        </Form.Item>
                        <Form.Item>
                          <ConfirmAction
                            title="Are you sure you want to approve this purchase request?"
                            onConfirm={submitApprove}
                            okText="Approve"
                            cancelText="Cancel"
                            okButtonProps={{ loading: loading === "approve" }}
                          >
                            <Button type="primary" loading={loading === "approve"}>
                              Approve
                            </Button>
                          </ConfirmAction>
                        </Form.Item>
                      </>
                    )}

                    {(add || edit) && (
                      <Form.Item>
                        <Button type="primary" loading={loading === "submit"} htmlType="submit">
                          {add ? "Submit" : "Update"}
                        </Button>
                      </Form.Item>
                    )}
                  </Col>

                </Row>
              </Form>
            </Col>
          </div>
        </Row>
      </Layout>
      <PersistentFooter />

      <ViewAttachmentModal
        showModal={!!showAttachment}
        attachmentUrl={showAttachment ?? ""}
        onCancel={() => setShowAttachment(null)}
      />

      <ReasonModal
        showModal={showDisapproveModal}
        onCancel={() => setShowDisapproveModal(false)}
        title="Please provide a reason for disapproval:"
        reasonValue={disapproveRemarks}
        onReasonChange={(e: any) => setDisapproveRemarks(e.target.value)}
        footer={[
          <Button key="cancel" onClick={() => setShowDisapproveModal(false)}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            loading={loading === "disapprove"}
            disabled={!disapproveRemarks.trim()}
            onClick={submitDisapprove}
          >
            Confirm Disapprove
          </Button>,
        ]}
      />
    </Layout>
  );
};

export default PurchaseRequestForm;